// #MODULE - BCOM SpeedBumpOverlay
//
// > Author: Christian Bonacore and Aori Nevo
// >
// > Create Date: March - 2014
// >
// ##DESCRIPTION: A panel with spinner indicating that the user is being redirected.
// It delegates the redirect to postFor.  Will render OopsOverlay on a
// certain set of failures.
//
import $ from 'jquery';
import { fireTag } from '@common-vue/util/src/modules/tagManager';
import CacheManager from '@component/common/src/util/CacheManager';
import TagManagerUtil from '@component/common/src/util/TagManagerUtil';
import * as Globals from '@component/common/src/util/Globals';
import Radio from '../../util/radio';
import { Actions } from '../../util/Constants';
import SpeedBumpOverlayMessages from './i18n/SpeedBumpOverlayMessages';
import ModalDialog from '../../util/ModalDialog';
import { isDateTimeExpired } from '../../util/rsa';
import { quickBagXAPI } from '../bagxapi';

let speedBumpOverlayRadio;
class SpeedBumpOverlay {
  // ###Method initialize( options ) - constructor
  //
  // > parameters
  // >
  // + *options* - To be passed to the postFor call. Please refer to
  // + that module.
  //
  // > returns
  // >
  // + a new SpeedBumpOverlay view
  constructor(options) {
    this.template = options.template;
    this.postFor = options.postFor;
    this.options = options || {};
    this.isNoCardsError = this.options.autoOpen && !this.options.hasCreditCard;
    this.isAutoRedirect = !this.isNoCardsError;
    this.activeElement = options.activeElement;

    // it allows a different window object to be passed.
    // for testing, for example.
    this.windowWrap = this.options.window || window;


    if (fireTag && this.options.tealium) {
      const utagData = {};
      const tealiumAttributes = this.options.tealium;

      utagData.account_action = tealiumAttributes.accountAction || '';
      utagData.event_name = tealiumAttributes.eventName || tealiumAttributes.event_name;

      if (tealiumAttributes.linkName || tealiumAttributes.link_name) {
        utagData.link_name = tealiumAttributes.linkName || tealiumAttributes.link_name;
      }
      let extendToken = false;
      if (this.options && this.options.model && this.options.model.attributes) {
        extendToken = this.options.extendToken || this.options.model.attributes.extendToken;
      }
      if (!extendToken) {
        fireTag('link', utagData);
      }
    }

    if (this.options.action) {
      // Define behavior of panel
      if (this.isAutoRedirect) {
        speedBumpOverlayRadio = Radio.channel('speedBumpOverlay');
        speedBumpOverlayRadio.on('remove', this.destroy.bind(this));
      }
    } else {
      console.warn('SpeedBumpOverlay created with no action.');
    }

    this.data = {
      i18n: SpeedBumpOverlayMessages,
      isAutoRedirect: this.isAutoRedirect,
      isNoCardsError: this.isNoCardsError,
    };
  }

  // ###Method render() renders the view on screen, using the modal-overlay component.
  // It also preloads the postFor component, setting a proper promise.
  //
  // > parameters
  // >
  // + *none*
  //
  // > returns
  // >
  // + as usual, a reference to itself, to encourage chaining.
  async render() {
    let title = '';
    if (!this.isNoCardsError && !this.isAutoRedirect) {
      ({ title } = SpeedBumpOverlayMessages);
    }

    // create overlay using template
    this.overlay = new ModalDialog({
      id: 'speed-bump-overlay',
      destroyOnClose: true,
      title,
      content: this.template(this.data),
    });
    await this.overlay.open();

    // Hide X close button, only show if there is an error
    const headerWrapper = document.querySelector('.header-wrapper');
    if (headerWrapper) headerWrapper.style.display = 'none';

    const overlayElement = document.querySelector('#speed-bump-overlay');
    if (overlayElement) {
      overlayElement.querySelectorAll('.continue').forEach((el) => {
        el.addEventListener('click', this.continueAction.bind(this));
      });

      overlayElement.querySelectorAll('.addMyCard').forEach((el) => {
        el.addEventListener('click', this.addMyCardAction.bind(this));
      });

      overlayElement.querySelectorAll('.header-wrapper').forEach((el) => {
        el.style.display = 'none';
      });
    }

    // This promise will be resolved when the require call
    // retrieves the postFor function.
    // It exposes a done callback that gets executed
    // when it resolves.
    // redirect automatically
    if (this.isAutoRedirect) {
      this.continueAction();
    }

    return this;
  }

  // ###Method destroy( e ) - destroy the modal. Executed when the user
  // clicks the cancel button.
  //
  // > parameters
  // >
  // + *e* - The browser click event passed by jquery.
  //
  // > returns
  // >
  // + *nothing*
  async destroy(e) {
    // prevent a redirect to '#'
    if (e) {
      e.preventDefault();
    }

    if (this.overlay) {
      await this.overlay.destroy();
      this.overlay = null;
    }

    /* D-22274: triggering focusout so that links with tooltip will hide the tooltip on close of speedbump */
    if (this.options && this.options.$calledByEl) {
      const focusOutEvent = new Event('focusout');
      this.options.$calledByEl.dispatchEvent(focusOutEvent);
    }

    if (this.activeElement) {
      this.activeElement.focus();
    }
  }


  // ###Method continueAction( e ) - Continues to the desired action,
  // when the user clicks the continue button.
  //
  // > parameters
  // >
  // + *e* - The browser click event passed by jquery.
  //
  // > returns
  // >
  // + *nothing*
  //
  continueAction(e) {
    const { options } = this;
    const { action } = options;

    if (e) {
      e.preventDefault();
    }

    if (!action) {
      return false;
    }
    return this.postFor(options);
  }

  async addMyCardAction(e) {
    e.preventDefault();
    await this.destroy();
    const SpeedBumpOverlayClass = this.constructor;
    // do a new SpeedBump call
    new SpeedBumpOverlayClass({
      action: 'addCard',
    }).render();
  }

  // Class properties
  // Publishes the default class(css) on class(00) level.

  // init is a class method that initializes the SpeedBumpOverlay for every button
  // that has data-action attribute inside the container it's passed (defaults to body.)
  // It works for existing and to-be-created elements inside that container.
  //
  // Example usage:
  //  You have a div#special-div with two links that should raise a speedBumpOverlay.
  //  Both links have data-action attributes. Just do the following:
  //  SpeedBumpOverlay.init($("#special-div"));
  //
  static init($container, options = {}) {
    const SpeedBumpOverlay = this; // eslint-disable-line no-shadow
    // create a new instance of SpeedBump without an event
    if (options.autoOpen) {
      new SpeedBumpOverlay(options).render();
      return;
    }
    // updating fix for all browsers
    if (window) { // eslint-disable valid-typeof
      window.onpageshow = function (event) {
        if (event.persisted) {
          document.body.innerHTML = '';
          window.location.reload();
        }
      };
    }
    // If not valid $ selector selecting something
    if (!$container || !$container.get || !$container.get(0)) {
      $container = $('body'); // eslint-disable-line no-param-reassign
    }

    // create a new instance of SpeedBump for links with data-action property
    $container.on('click', '.citi-redirect', async function (e) {
      const el = $(this);
      const eventTarget = ((e && e.currentTarget) || {}).id;
      // converting single quotes into double and then adding double quotes to attribute values
      let tealiumData = {};
      if (e.currentTarget && e.currentTarget.dataset && e.currentTarget.dataset.tealium) {
        tealiumData = JSON.parse(e.currentTarget.dataset.tealium.replace(/^"|"$/g, '`'));
      }
      if (options.model && options.pageApp) {
        const { callbackFunction } = options.model.attributes;
        const extendToken = options.model.attributes.extendToken || Globals.getValue('extendToken');
        // needed for firing tags
        if (this.options) {
          this.options.extendToken = extendToken;
        }
        const openStepUp = extendToken && !callbackFunction;
        if (openStepUp) {
          options.model.set({
            stepUpLoginTarget: eventTarget,
            pageName: options.pageName,
            // once the step-up Auth is completed, this function will trigger to save the form data
            callbackFunction: options.view.redirectCitiPostStepUp.bind(options.view, e),
          });
          options.pageApp.channel.trigger('bodyapp:overlay:stepUpAuthentication', options.model);
          TagManagerUtil.fireTag('view', {
            page_name: tealiumData.pageName || tealiumData.page_name,
            page_type: 'step up login',
            link_name: tealiumData.linkName || tealiumData.link_name,
            event_name: tealiumData.eventName || tealiumData.event_name,
          });
          return;
        }
      }
      e.preventDefault();
      // close Speed bump overlay instances
      if (this.overlay) {
        await this.overlay.destroy();
        this.overlay = null;
      }
      // update options
      options.action = el.data('action'); // eslint-disable-line no-param-reassign
      options.params = el.data('params'); // eslint-disable-line no-param-reassign
      options.headers = el.data('headers'); // eslint-disable-line no-param-reassign
      options.tealium = el.data('tealium'); // eslint-disable-line no-param-reassign
      options.$calledByEl = el; // eslint-disable-line no-param-reassign
      options.activeElement = document.activeElement; // eslint-disable-line no-param-reassign

      // clear creditCardApply session
      const { isCCBannerComponent, action } = options;

      if (isCCBannerComponent && sessionStorage && action === 'applyNow') {
        const BRAND_NAME = (process.env.ENTRY_POINT === 'bcom') ? 'bloomingdales' : 'macys';
        const creditCardApply = JSON.parse(sessionStorage.getItem('creditCardApply'));
        const isMcomBrand = process.env.ENTRY_POINT === 'mcom';
        const source = options.params ? options.params._source : '';
        const formatSource = `${source.replace('-', ' ')} banner`;
        TagManagerUtil.fireTag('link', {
          event_name: (`cc apply now ${isMcomBrand ? '' : formatSource}`).trim(),
          link_name: (`cc apply now ${isMcomBrand ? formatSource : ''}`).trim(),
        });
        // Trigger Tealium tag for prequal service call
        if (creditCardApply.prequalStatus && creditCardApply.prequalStatus.statusFlag === 'Q') {
          TagManagerUtil.fireTag('link', {
            event_name: `apply now prequalified ${BRAND_NAME} card`,
            link_name: `apply now prequalified ${BRAND_NAME} card`,
          });
        }
        try {
          if (sessionStorage.getItem('creditCardApply')) {
            sessionStorage.removeItem('creditCardApply');
          }
          if (options.headers) {
            const headers = typeof options.headers === 'string' ? JSON.parse(options.headers) : options.headers;
            if (headers.rsa === 'true') {
              delete headers.rsa;
            } else {
              const qbRest = JSON.parse(sessionStorage.getItem('qbRest')) || {};
              const qbRestExp = sessionStorage.getItem('qbRestExp') || null;

              if (qbRest && qbRestExp && !isDateTimeExpired(qbRestExp)) {
                const qbRestValue = qbRest.meta.price.find(o => o.type === 'total');
                if (qbRestValue && qbRestValue.bagAmountWithRSA !== undefined) headers.requestedSalesAmount = qbRestValue.bagAmountWithRSA;
              } else {
                headers.requestedSalesAmount = '';
                const response = await quickBagXAPI(headers.bagGuid);
                if (response && response.meta && response.meta.price) {
                  const qbRestValue = response.meta.price.find(o => o.type === 'total');
                  CacheManager.setSession('qbRest', response, 15);
                  if (qbRestValue && qbRestValue.bagAmountWithRSA !== undefined) headers.requestedSalesAmount = qbRestValue.bagAmountWithRSA;
                }
              }
            }
            options.headers = JSON.stringify(headers);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
        }
      }

      // For this scenario, if PageApp is needed, it should be passed in the options when calling SpeedBumpOverlay.init
      if (options.PageApp && options.action === 'addCard' && options.activeElement.id !== 'cc-add-card') {
        options.PageApp.channel.trigger(Actions.card.add);
      }

      const speedBumpOverlay = new SpeedBumpOverlay(options);
      speedBumpOverlay.render();
    });
  }
}

export default SpeedBumpOverlay;
export { SpeedBumpOverlay };
